import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import Option from '@/components/form/types/Option.ts';
import { CatalogRefDto } from '@/features/dashboard/availability/types/AvailabilityDto.ts';
import { FieldType } from '@/components/table/_fieldDefinitions/FieldType.ts';
import {
  BillRowAction,
  BillRowVm,
} from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';
import { AddManualLineParams } from '@/features/dashboard/bookingChanges/types/ChangeViewModel.ts';
import {
  BillLineRef,
  BookingBill,
  billFieldsOf,
  hasBillField,
} from '@/features/dashboard/bookingChanges/types/BookingBill.ts';
import { BillField } from '@/features/dashboard/bookingChanges/types/dto/BillField.ts';
import { ClientBillField } from '@/features/dashboard/bookingChanges/types/dto/ClientBillField.ts';
import { isAddressLine } from '@/features/dashboard/bookingChanges/types/dto/ClientBillField.utils.ts';
import { ParticipantBillField } from '@/features/dashboard/bookingChanges/types/dto/ParticipantBillField.ts';
import { AddressDto } from '@/types/AddressDto.ts';
import { PhoneNumberDto } from '@/types/PhoneNumberDto.ts';
import {
  ChangeItemDto,
  ChangeItemType,
} from '@/features/dashboard/bookingChanges/types/dto/ChangeItemDto.ts';
import { DetailsMode } from '@/features/dashboard/bookingChanges/routes/detailsSearch.ts';
import { notifyActionUnavailable } from '@/features/dashboard/bookingChanges/utils/actionPlaceholder.ts';
import { resolveNewVariantIds } from '@/features/dashboard/bookingChanges/utils/draftItems.ts';
import useChangeDraft from '@/features/dashboard/bookingChanges/hooks/useChangeDraft.ts';
import useBookingBill from '@/features/dashboard/bookingChanges/hooks/useBookingBill.ts';
import usePatchDraft from '@/features/dashboard/bookingChanges/hooks/usePatchDraft.ts';
import { MANUAL_DISCOUNT_LABEL, MANUAL_LINE_LABEL } from './editFieldCopy';
import {
  BillLineLocation,
  buildChangeItems,
  findChangeLine,
  findSlotItems,
  isEditableLine,
  mergeDraftItems,
  newManualLineLocation,
} from './changeItemDraft';
import { EditFieldFormData } from './editFieldSchema';
import { resolveFieldOptions, seatRefsOf } from './fieldOptions';
import { customFieldTypeOf } from './fieldTypes';

/** Wiersz otwarty w modalu edycji wraz ze wszystkim, czego modal potrzebuje. */
export interface EditFieldTarget {
  row: BillRowVm;
  location: BillLineLocation;
  sectionTitle: string;
  type: FieldType;
  /** Pola kontraktu opisywane przez wiersz — modal pyta o każde z osobna. */
  fields: BillField[];
  options: Option[];
  /** Wyjazd, którego katalogu dotyczy wiersz — potrzebny do pytania o obłożenie. */
  tourId: string;
  /** Warianty do sprawdzenia pod kątem miejsc; pusta lista = nie ma czego pilnować. */
  seatRefs: CatalogRefDto[];
  withAmount: boolean;
  defaultValues: EditFieldFormData;
  /**
   * Pozycja siedzi już w szkicu i wolno ją stamtąd zdjąć. Dotyczy wyłącznie
   * pozycji ręcznych: pozostałe wiersze rachunek ma tak czy owak, więc
   * „usunięcie” znaczyłoby dla nich powrót do wartości sprzed zmiany.
   */
  canRemove: boolean;
  /**
   * Pozycja przyszła z przycisku „+ Zniżka” — formularz podpowiada wtedy kwotę
   * ujemną. Podpowiedź, nie ograniczenie: obie strony wolno wpisać w każdej
   * z tych pozycji, bo o koszcie i zniżce decyduje sam znak kwoty.
   */
  discount: boolean;
}

export interface UseEditFieldResult {
  target: EditFieldTarget | null;
  isSaving: boolean;
  /** Akcja wiersza: „Zmień” / „Edytuj” otwiera modal, „Otwórz” nawiguje. */
  openRow: (row: BillRowVm) => void;
  /** „+ Wolna linia” / „+ Zniżka” — otwiera formularz pustej pozycji ręcznej. */
  addManualLine: (params: AddManualLineParams) => void;
  close: () => void;
  submit: (values: EditFieldFormData) => void;
  /** Zdejmuje pozycję ze szkicu — wypada z listy przy najbliższym PATCH-u. */
  remove: () => void;
}

/** Bieżące chipy wiersza — bez tych, które szkic zdejmuje. */
const activeChips = (row: BillRowVm): string | null =>
  row.chips
    ? row.chips
        .filter((chip) => !chip.removed)
        .map((chip) => chip.label)
        .join(', ')
    : null;

/**
 * Wartość wybrana wcześniej w szkicu — listy operują na UUID-ach, nie nazwach.
 * Pole listy rozwijanej trzyma swój UUID w wartości niestandardowej, więc
 * wyciąga się go po identyfikatorze pola.
 */
const slotVariantId = (
  slotItem: ChangeItemDto | undefined,
  ref: BillLineRef | null,
): string | null => {
  switch (slotItem?.type) {
    case ChangeItemType.OPTION:
    case ChangeItemType.TRANSPORT:
      return slotItem.newVariantId;
    case ChangeItemType.INSURANCE:
      return slotItem.newInsuranceId;
    case ChangeItemType.CUSTOM_VALUES: {
      const value = ref?.targetId
        ? slotItem.newCustomValues?.[ref.targetId]
        : undefined;
      return value?.type === FieldType.DROPDOWN ? value.value : null;
    }
    default:
      return null;
  }
};

const slotBirthDate = (slotItem: ChangeItemDto | undefined): string | null =>
  slotItem?.type === ChangeItemType.PARTICIPANT_DATA ||
  slotItem?.type === ChangeItemType.CLIENT_DATA
    ? slotItem.newBirthDate
    : null;

const slotPriceOverride = (slotItem: ChangeItemDto | undefined) =>
  slotItem && 'priceOverride' in slotItem ? slotItem.priceOverride : null;

const slotPriceNote = (slotItem: ChangeItemDto | undefined): string =>
  slotItem && 'priceNote' in slotItem ? (slotItem.priceNote ?? '') : '';

/** Telefon wpisany wcześniej w szkicu; `null` = formularz startuje z rachunku. */
const slotPhoneNumber = (
  slotItem: ChangeItemDto | undefined,
): PhoneNumberDto | null =>
  slotItem?.type === ChangeItemType.CLIENT_DATA
    ? slotItem.newPhoneNumber
    : null;

/** Adres wpisany wcześniej w szkicu; `null` = formularz startuje z rachunku. */
const slotAddress = (slotItem: ChangeItemDto | undefined): AddressDto | null =>
  slotItem?.type === ChangeItemType.CLIENT_DATA ? slotItem.newAddress : null;

/** Opis wpisany wcześniej w szkicu — etykieta wiersza pozycji ręcznej. */
const slotDescription = (slotItem: ChangeItemDto | undefined): string =>
  slotItem?.type === ChangeItemType.MANUAL_LINE ? slotItem.description : '';

/**
 * Imię i nazwisko wpisane wcześniej w szkicu. Pola są niezależne, więc każde
 * osobno może być puste — `null` znaczy „formularz startuje z rachunku”.
 */
const slotPersonName = (
  slotItem: ChangeItemDto | undefined,
): { firstName: string | null; lastName: string | null } | null =>
  slotItem?.type === ChangeItemType.PARTICIPANT_DATA ||
  slotItem?.type === ChangeItemType.CLIENT_DATA
    ? { firstName: slotItem.newFirstName, lastName: slotItem.newLastName }
    : null;

/** Wiek uczestnika na pierwszy dzień turnusu — próg cenowy wariantów. */
const participantAgeOf = (
  bill: BookingBill,
  participantId: string | null,
): number | null =>
  bill.participants.find(
    (participant) => participant.participantId === participantId,
  )?.age ?? null;

/**
 * Typ pola dla edytowanego wiersza.
 *
 * Typ niesie sam wiersz (`BillLine.type`) — jedyne, co dokłada modal, to
 * zawężenie wartości niestandardowej do czterech typów, jakie przyjmuje
 * kontrakt zapisu.
 */
const resolveEditFieldType = (location: BillLineLocation): FieldType => {
  const { line } = location;

  return line.ref?.kind === ChangeItemType.CUSTOM_VALUES
    ? customFieldTypeOf(line.type)
    : line.type;
};

/**
 * Wartości startowe formularza.
 *
 * Wiersz niesie wartość **po** uwzględnieniu szkicu, więc ponowne wejście
 * w pozycję już zmienioną pokazuje to, co operator wybrał poprzednio. Wyjątkiem
 * są listy wariantów: wiersz zna nazwę, a formularz potrzebuje identyfikatora,
 * ten bierzemy z pozycji szkicu albo z odsyłacza wiersza rachunku.
 */
const resolveDefaultValues = (
  row: BillRowVm,
  location: BillLineLocation,
  type: FieldType,
  slotItems: ChangeItemDto[],
  bill: BookingBill,
): EditFieldFormData => {
  const slotItem = slotItems[0];
  const participant = bill.participants.find(
    (candidate) => candidate.participantId === location.participantId,
  );
  const billBirthDate = hasBillField(
    location.line.ref,
    ParticipantBillField.BIRTH_DATE,
  )
    ? participant?.birthDate
    : undefined;
  // Wiersz nazwy startuje od tego, co szkic już w nim zmienił, a bez szkicu —
  // od danych osoby z rachunku. Uczestnik albo zapisujący, zależnie od wiersza.
  const namePerson = participant ?? bill.client;
  const name = slotPersonName(slotItem);
  const isoDate = slotBirthDate(slotItem) ?? billBirthDate ?? null;
  const priceOverride = slotPriceOverride(slotItem);

  // Wiersz wybierany z listy ma za wartość identyfikator z katalogu, a nie
  // napis — i dotyczy to tak samo wariantu, jak opcji pola listy rozwijanej.
  const fromCatalog =
    type === FieldType.DROPDOWN || type === FieldType.MULTISELECT;

  const value = row.manual
    ? // Kwota pozycji ręcznej siedzi w nadpisaniu, a nie w wierszu rachunku:
      // wiersz powstaje razem z pozycją i sam żadnej kwoty nie ma.
      String(priceOverride?.amount ?? '')
    : fromCatalog
      ? (slotVariantId(slotItem, location.line.ref) ??
        location.line.ref?.currentVariantId ??
        '')
      : type === FieldType.MONEY_VALUE
        ? String(row.amount[0]?.amount ?? location.line.amount[0]?.amount ?? '')
        : (activeChips(row) ?? row.value ?? '');

  const address = isAddressLine(location.line.ref)
    ? (slotAddress(slotItem) ?? bill.client.address)
    : null;

  return {
    value,
    date: type === FieldType.DATE && isoDate ? dayjs(isoDate).toDate() : null,
    // Wybór wielokrotny operuje na identyfikatorach wariantów, a nie nazwach —
    // stan wyjściowy bierzemy z wiersza wraz z tym, co szkic już w nim zmienił.
    values: resolveNewVariantIds(location.line, slotItems) ?? [],
    street: address?.street ?? '',
    postalCode: address?.postalCode ?? '',
    city: address?.city ?? '',
    firstName: name?.firstName ?? namePerson.firstName,
    lastName: name?.lastName ?? namePerson.lastName,
    phoneNumber: slotPhoneNumber(slotItem) ??
      bill.client.phoneNumber ?? { dialCode: '', number: '' },
    description: slotDescription(slotItem),
    priceOverridden: priceOverride !== null,
    price: priceOverride ? String(priceOverride.amount) : '',
    priceNote: slotPriceNote(slotItem),
    // Waluta wiersza; pozycja ręczna dopiero zakładana nie ma jeszcze kwoty,
    // więc startuje od waluty rezerwacji — operator zmieni ją w formularzu.
    currency:
      location.line.amount[0]?.currency ??
      bill.totals[0]?.currency ??
      Currency.PLN,
  };
};

/**
 * Wpięcie akcji wiersza w szkic paczki.
 *
 * Modal edycji potrzebuje trzech rzeczy naraz — wiersza rachunku (bo pozycja
 * paczki operuje na `BillLine.ref`), otwartego szkicu (bo PATCH podmienia
 * **całą** listę pozycji) i typu pola (bo kontrakt go nie niesie). Hook spina
 * je w jednym miejscu, żeby trzy ekrany operatora robiły to tak samo.
 */
const useEditField = (bookingId: string): UseEditFieldResult => {
  const navigate = useNavigate();
  const billQuery = useBookingBill(bookingId);
  const { change } = useChangeDraft(bookingId);
  const patchDraft = usePatchDraft(bookingId);
  const [target, setTarget] = useState<EditFieldTarget | null>(null);

  const close = () => setTarget(null);

  const openRow = (row: BillRowVm) => {
    // Pozycje sterowane z poziomu rezerwacji (zniżki) nie mają własnego
    // formularza — prowadzą na ekran, na którym da się je ruszyć.
    if (row.locked) {
      navigate({
        to: '/app/bookings/$bookingId/booking',
        params: { bookingId },
        search: { mode: DetailsMode.EDIT },
      });
      return;
    }

    const bill = billQuery.data;
    // Wiersz pozycji ręcznej dopisał sam szkic, więc rachunek go nie zna —
    // szukamy w jednym i w drugim.
    const location = bill
      ? findChangeLine(bill, change?.items ?? [], row.id)
      : null;

    if (!bill || !location || !isEditableLine(location)) {
      notifyActionUnavailable(`Zmiana pozycji „${row.label}”`);
      return;
    }

    // Cena wariantu zależy od progu wiekowego, a wiersz stoi przy konkretnej
    // osobie — próg wybiera się jej wiekiem, zamiast brać pierwszy z brzegu.
    const options = resolveFieldOptions(
      location,
      bill.catalog,
      participantAgeOf(bill, location.participantId),
    );
    const type = resolveEditFieldType(location);
    const slotItems = change ? findSlotItems(change.items, location) : [];

    setTarget({
      row,
      location,
      sectionTitle: location.section.title,
      type,
      fields: billFieldsOf(location.line.ref),
      options,
      tourId: bill.tourId,
      seatRefs: seatRefsOf(location, bill.catalog),
      withAmount: location.section.withAmounts,
      defaultValues: resolveDefaultValues(row, location, type, slotItems, bill),
      canRemove: !!row.manual && slotItems.length > 0,
      // Pozycja już zapisana mówi o swoim znaku samą kwotą — podpowiadać nie ma
      // czego.
      discount: false,
    });
  };

  /**
   * Dopisanie wolnej linii. Wiersz i pozycja dostają jeden identyfikator, bo
   * tylko po nim da się je później związać — pozycja ręczna nie ma w rachunku
   * niczego, do czego mogłaby się przypiąć.
   */
  const addManualLine = ({
    participantId = null,
    sectionId = null,
    discount = false,
  }: AddManualLineParams) => {
    const bill = billQuery.data;
    const lineId = crypto.randomUUID();
    const label = discount ? MANUAL_DISCOUNT_LABEL : MANUAL_LINE_LABEL;
    const location = bill
      ? newManualLineLocation(bill, participantId, sectionId, lineId, label)
      : null;

    if (!bill || !location) {
      notifyActionUnavailable(label);
      return;
    }

    const row: BillRowVm = {
      id: lineId,
      label,
      type: FieldType.MONEY_VALUE,
      // Wiersz powstaje razem z pozycją, więc żadnej kwoty jeszcze nie ma.
      amount: [],
      oldAmount: [],
      manual: true,
      draftOnly: true,
      action: BillRowAction.ADD,
    };

    setTarget({
      row,
      location,
      sectionTitle: location.section.title,
      // Wolna linia niesie kwotę i nic poza nią — pola opisu pyta modal,
      // bo wynikają z samego `row.manual`, a nie z typu pola.
      type: FieldType.MONEY_VALUE,
      fields: [],
      options: [],
      tourId: bill.tourId,
      seatRefs: [],
      withAmount: location.section.withAmounts,
      defaultValues: resolveDefaultValues(
        row,
        location,
        FieldType.MONEY_VALUE,
        [],
        bill,
      ),
      canRemove: false,
      discount,
    });
  };

  const submit = (values: EditFieldFormData) => {
    if (!target) {
      return;
    }
    if (!change) {
      toast.error('Brak otwartego szkicu', {
        description: 'Włącz tryb edycji, żeby zapisać zmianę w paczce.',
      });
      return;
    }

    const slotItems = findSlotItems(change.items, target.location);
    const nextItems = buildChangeItems({
      location: target.location,
      type: target.type,
      values,
      slotItems,
      newItemId: () => crypto.randomUUID(),
    });

    // Pusty wynik przy pustym slocie znaczy, że kontrakt paczki tej zmiany nie
    // wyraża. Pusty wynik przy zajętym slocie to powrót do stanu sprzed szkicu —
    // pozycje po prostu z niego wychodzą.
    if (nextItems.length === 0 && slotItems.length === 0) {
      notifyActionUnavailable(`Zmiana pozycji „${target.row.label}”`);
      close();
      return;
    }

    patchDraft.mutate(
      {
        changeId: change.id,
        items: mergeDraftItems(change.items, nextItems, slotItems),
      },
      {
        onSuccess: () => {
          close();
          toast.success('Zmiana zapisana w szkicu');
        },
      },
    );
  };

  /**
   * Usunięcie pozycji ze szkicu. PATCH podmienia całą listę, więc „skasowanie”
   * to po prostu wysłanie jej bez tej pozycji — osobnej metody kontrakt nie ma.
   */
  const remove = () => {
    if (!target || !change) {
      return;
    }

    const slotItems = findSlotItems(change.items, target.location);
    if (slotItems.length === 0) {
      close();
      return;
    }

    patchDraft.mutate(
      {
        changeId: change.id,
        items: mergeDraftItems(change.items, [], slotItems),
      },
      {
        onSuccess: () => {
          close();
          toast.success('Pozycja usunięta ze szkicu');
        },
      },
    );
  };

  return {
    target,
    isSaving: patchDraft.isPending,
    openRow,
    addManualLine,
    close,
    submit,
    remove,
  };
};

export default useEditField;
